import * as React from 'react'
import { useQuery } from '@apollo/react-hooks'
import dynamic from 'next/dynamic'

import {
  EnterpriseContext,
  i18n,
  MessageArea,
  Routes,
  SUPPORTED_FEATURES,
  useRequestConfig,
  useSiteConfig,
  useTheme,
} from '@thg-commerce/enterprise-core'
import { ADAChatContent } from '@thg-commerce/enterprise-core/src/Layouts/AccountLayout/ADAChatContent'
import { LiveChatContent } from '@thg-commerce/enterprise-core/src/Layouts/AccountLayout/LiveChatContent'
import { RingCentralContent } from '@thg-commerce/enterprise-core/src/Layouts/AccountLayout/RingCentralContent'
import { EnterpriseThemeInterface } from '@thg-commerce/enterprise-theme'
import {
  AccordionContainerProps,
  HorizontalAlignment,
} from '@thg-commerce/enterprise-widget-accordion-container'
import {
  AriaMessageType,
  Button,
  PlatformMessage,
} from '@thg-commerce/gravity-elements'
import { Grid, useScrollLock } from '@thg-commerce/gravity-system'

import { Features as FEATURES_QUERY } from '../../widgets/graphql/Query/Features.graphql'

import { dataTracking } from './dataTracking'
import { HelpCenterCollectionSearch } from './HelpCenterCollectionSearch'
import {
  AccessibilityBackButtonWrapper,
  BackButton,
  LeftRailTitle,
  LiveChatWrapper,
  StyledAccordionContainer,
  StyledAccordionContent,
  StyledAccordionSearchContainer,
  StyledAccordionSearchContent,
  StyledAltText,
  StyledButton,
  StyledGridItem,
  StyledIcon,
  StyledLi,
  StyledLink,
  StyledLinkContent,
  StyledLiWrapper,
  StyledNotFoundText,
  StyledSeparator,
  Subtitle,
  TextContainer,
  Title,
} from './styles'

const ChevronLeft = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/ChevronLeft'),
)

const FacebookOutline = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/FacebookOutline'),
)

const TwitterOutline = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/TwitterOutline'),
)

const EmailOutline = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/EmailOutline'),
)

const Message = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/Message'),
)

const MessagesAppOutline = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/MessagesAppOutline'),
)

const PhoneOutline = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/PhoneOutline'),
)

export interface AccordionProps extends AccordionContainerProps {
  index?: number
}

interface AccordionNavItems {
  title?: string
  active?: boolean
}

export interface HelpCentreCollectionProps {
  accordions: AccordionProps[]
  className?: string
}

interface WrappedLinkProps {
  href: string
  content?: string
  children: React.ReactNode
  altText?: string
}

interface ChatAndMessageLinksProps {
  hasLivePerson?: boolean
  hasAdaChatbot?: boolean
  hasRingCentral?: boolean
  enabledAppleChat?: boolean
  i18nText: {
    appleBusinessId?: string
    messagesApp?: string
    message?: string
    viewMessages?: string
    contactUsNumber?: string | number
    contactUsText?: string
    contactUsAlttext?: string
  }
}

interface SocialMediaLinksProps {
  enabledSocialMedia?: boolean | string
  facebookUrl?: string | null
  twitterUrl?: string | null
  i18nText: {
    socialLinksTitle?: string
    facebook?: string
    twitter?: string
  }
  theme: EnterpriseThemeInterface
}

interface ChatContainerProps {
  hasLivePerson?: boolean
}

const WrappedLink = (props: WrappedLinkProps) => {
  const theme = useTheme()
  return (
    <StyledLiWrapper>
      <StyledLink href={props.href} target="_blank">
        <StyledIcon>{props.children}</StyledIcon>
        <StyledLinkContent
          textStyling={theme.widget.helpCenterCollection?.leftRail.content}
        >
          {props.content}
          {props.altText && <StyledAltText>{props.altText}</StyledAltText>}
        </StyledLinkContent>
      </StyledLink>
    </StyledLiWrapper>
  )
}

const showFeatures = (loading: boolean, featuresEnabled?: [string]) =>
  (!loading && featuresEnabled) || ['']

const showSocialMediaLinks = (
  isRequestFromChina: boolean,
  enabledFeatures: [string],
  facebookUrl?: string,
  twitterUrl?: string,
) =>
  !isRequestFromChina &&
  !enabledFeatures.includes(
    SUPPORTED_FEATURES.ACCOUNT_HOME_SOCIAL_MEDIA_HIDDEN,
  ) &&
  (facebookUrl || twitterUrl)

const showAppleBusinessChat = (
  enabledFeatures: [string],
  isAppleDevice: boolean,
) =>
  enabledFeatures.includes(SUPPORTED_FEATURES.APPLE_BUSINESS_CHAT) &&
  isAppleDevice

const showAccessibilityBackButton = (
  showBackButton: boolean,
  activeAccordion: AccordionProps | null,
) => showBackButton && activeAccordion

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = React.useState(value)

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)
    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

const DEFAULT_DEBOUNCE_DELAY = 400
const DEFAULT_MIN_SEARCH_LENGTH = 3

export const HelpCentreCollection = (props: HelpCentreCollectionProps) => {
  const i18nText = {
    pageTitle: i18n('general.help.center.page.title'),
    pageSubTitle: i18n('general.help.center.page.sub.title'),
    searchText: i18n('general.help.center.search.value.text'),
    leftRailTitle: i18n('general.help.center.left.rail.title'),
    customerSupportTitle: i18n('general.help.center.customer.support.title'),
    socialLinksTitle: i18n('general.help.center.social.links.title'),
    accordionSubTitle: i18n('general.help.center.accordion.sub.title'),
    viewMessages: i18n('myaccount.viewyourmessages.text'),
    contactUsText: i18n('myaccount.helpcentre.contact.text'),
    contactUsAlttext: i18n('myaccount.helpcentre.contact.alttext'),
    contactUsNumber: i18n('myaccount.helpcentre.contact.number'),
    messagesApp: i18n('account.navigation.messagesapp.text'),
    message: i18n('myaccount.message.text'),
    appleBusinessId: i18n('apple.chat.business.id'),
    facebook: i18n('account.sociallogin.facebook.text'),
    twitter: i18n('account.sociallogin.twitter.text'),
    back: i18n('general.help.center.back.text'),
    goBack: i18n('general.go.back.text'),
    noResultsFound: i18n('general.help.center.empty.search.results.text'),
    searchResults: i18n('content.searchresults.title'),
    externalRoute: i18n('general.external.route'),
  }

  const theme = useTheme()

  const {
    hasLivePerson,
    facebookUrl,
    twitterUrl,
    hasAdaChatbot,
    hasRingCentral,
    enableContactUsInfo,
    enableExternalCreateMessage,
  } = useSiteConfig()

  const { currentLocation } = React.useContext(EnterpriseContext)

  const { userAgent } = useRequestConfig()

  const isAppleDevice = userAgent?.includes('Mac OS')

  const isRequestFromChina = currentLocation.toLowerCase() === 'cn'

  const { data, loading } = useQuery<
    { features: { enabled: [string] } },
    { identifiers: string[] }
  >(FEATURES_QUERY, {
    variables: {
      identifiers: [
        SUPPORTED_FEATURES.ACCOUNT_HOME_SOCIAL_MEDIA_HIDDEN,
        SUPPORTED_FEATURES.APPLE_BUSINESS_CHAT,
      ],
    },
  })

  const [
    activeAccordion,
    setActiveAccordion,
  ] = React.useState<AccordionProps | null>(
    props.accordions &&
      props.accordions?.[0] && { ...props.accordions[0], index: 0 },
  )

  const [accordionNavItems, setAccordionNavItems] = React.useState<
    AccordionNavItems[]
  >(
    props.accordions?.map((accordion, index) => ({
      title: accordion.title,
      active: index === 0,
    })),
  )

  const [foundSearchResults, setFoundSearchResults] = React.useState<
    AccordionProps[] | null
  >(null)

  const [openedContent, setOpenedContent] = React.useState(false)
  const [contentTabIndex, setContentTabIndex] = React.useState(-1)
  const [showBackButton, setShowBackButton] = React.useState(false)
  const [showNoResultsMessage, setShowNoResultsMessage] = React.useState(false)
  const [activeSearch, setActiveSearch] = React.useState(false)

  const setScrollLock = useScrollLock()

  React.useEffect(() => {
    if (window.innerWidth && window.innerWidth < 600) {
      setScrollLock(openedContent, document, window)
    }
  }, [openedContent, setScrollLock])

  const itemsRef = React.useRef<HTMLButtonElement | null[]>([])
  const accordionRef = React.useRef<HTMLDivElement>(null)
  const resultsRef = React.useRef<HTMLDivElement>(null)
  const noResultsMessageRef = React.useRef<HTMLDivElement>(null)

  const [searchValue, setSearchValue] = React.useState<string>('')

  const debouncedSearchTerm = useDebounce(searchValue, DEFAULT_DEBOUNCE_DELAY)

  const handleActiveAccordionOnClick = (index: number) => {
    setSearchValue('')
    setFoundSearchResults(null)
    setActiveAccordion({
      ...props.accordions[index],
      index,
    })
    setAccordionNavItems(
      accordionNavItems.map((accordion, accordionIndex) => ({
        title: accordion.title,
        active: index === accordionIndex,
      })),
    )
    setOpenedContent(true)
  }

  const handleActiveAccordionOnKeyPress = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (e.key === 'Enter') {
      handleActiveAccordionOnClick(index)
      setContentTabIndex(0)
      accordionRef.current?.focus()
      setShowBackButton(true)
    } else {
      setContentTabIndex(-1)
      setShowBackButton(false)
    }
  }

  const handleBackAction = (index: number) => {
    const next = itemsRef.current && itemsRef.current[index]
    if (next) {
      next.focus()
    }
    setContentTabIndex(-1)
    setShowBackButton(false)
  }

  const enabledFeatures = showFeatures(loading, data?.features.enabled)
  const enabledSocialMedia = showSocialMediaLinks(
    isRequestFromChina,
    enabledFeatures,
    facebookUrl,
    twitterUrl,
  )
  const enabledAppleChat = showAppleBusinessChat(enabledFeatures, isAppleDevice)
  const enabledAccessibilityBackButton = showAccessibilityBackButton(
    showBackButton,
    activeAccordion,
  )

  const setDefaultActiveAccordion = React.useCallback(() => {
    setActiveAccordion(
      props.accordions?.[0] && { ...props.accordions[0], index: 0 },
    )
    setAccordionNavItems(
      accordionNavItems.map((accordion, accordionIndex) => ({
        title: accordion.title,
        active: accordionIndex === 0,
      })),
    )
  }, [accordionNavItems, props.accordions])

  React.useEffect(() => {
    const trimmedSearchValue = debouncedSearchTerm
      .trim()
      .replace(/[^\w\s?']/gi, '')
    const usedTerm =
      trimmedSearchValue.length >= DEFAULT_MIN_SEARCH_LENGTH
        ? trimmedSearchValue
        : ''
    setActiveSearch(!!usedTerm)

    if (usedTerm) {
      const searchedWords = usedTerm.split(' ').filter((x) => x.length > 0)
      const foundSearchResultItems = props.accordions.reduce(
        (result, accordion) => {
          const foundItems = accordion.items.filter(
            (item) =>
              !!searchedWords.filter((word) =>
                new RegExp(`(>[^<]*?|^[^<]*?)(${word})([^<]*?)`, 'i').test(
                  item.title,
                ),
              )?.length,
          )

          if (foundItems?.length) {
            const highlightedFoundItems = foundItems.map((item) => {
              const highlightedTitle = searchedWords.reduce((title, word) => {
                return title.replace(
                  new RegExp(`(>[^<]*?|^[^<]*?)(${word})([^<]*?)`, 'i'),
                  `$1<span>$2</span>$3`,
                )
              }, item.title)

              return {
                ...item,
                title: highlightedTitle,
              }
            })
            return [...result, { ...accordion, items: highlightedFoundItems }]
          }

          return result
        },
        [] as AccordionProps[],
      )

      setFoundSearchResults(
        foundSearchResultItems?.length ? foundSearchResultItems : null,
      )
      setShowNoResultsMessage(!foundSearchResultItems?.length)
      setActiveAccordion(null)
      setAccordionNavItems(
        accordionNavItems.map((accordion) => ({
          title: accordion.title,
          active: false,
        })),
      )
    } else {
      setFoundSearchResults(null)
      setShowNoResultsMessage(false)
      setDefaultActiveAccordion()
    }

    if (trimmedSearchValue) {
      dataTracking.searchTermHandler(trimmedSearchValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm])

  const handleInputOnChange = (value) => {
    setSearchValue(value)
  }

  const handleClearInput = () => {
    setSearchValue('')
    setFoundSearchResults(null)
    setShowNoResultsMessage(false)
    if (!activeAccordion) {
      setDefaultActiveAccordion()
    }
  }

  const externalRouteLink = i18nText.externalRoute

  const ChatAndMessageLinks = ({
    hasLivePerson,
    hasAdaChatbot,
    enabledAppleChat,
    i18nText,
  }: ChatAndMessageLinksProps) => {
    return (
      <ul>
        {hasRingCentral && (
          <StyledLiWrapper>
            <RingCentralContent />
          </StyledLiWrapper>
        )}
        {hasLivePerson && !hasAdaChatbot && (
          <StyledLiWrapper>
            <LiveChatContent outline />
          </StyledLiWrapper>
        )}
        {hasAdaChatbot && !hasLivePerson && (
          <StyledLiWrapper>
            <ADAChatContent outline />
          </StyledLiWrapper>
        )}
        {enabledAppleChat && (
          <WrappedLink
            href={`//bcrw.apple.com/urn:biz:${i18nText.appleBusinessId}`}
            content={i18nText.messagesApp}
          >
            <MessagesAppOutline />
          </WrappedLink>
        )}
        <WrappedLink
          href={
            enableExternalCreateMessage
              ? externalRouteLink
              : Routes.AccountNewMessage
          }
          content={i18nText.message}
        >
          <Message />
        </WrappedLink>
        {!enableExternalCreateMessage && (
          <WrappedLink
            href={Routes.AccountViewMessages}
            content={i18nText.viewMessages}
          >
            <EmailOutline />
          </WrappedLink>
        )}
        {enableContactUsInfo && (
          <WrappedLink
            href={`tel:${i18nText.contactUsNumber}`}
            content={i18nText.contactUsText}
            altText={i18nText.contactUsAlttext}
          >
            <PhoneOutline />
          </WrappedLink>
        )}
      </ul>
    )
  }

  const SocialMediaLinks = ({
    enabledSocialMedia,
    facebookUrl,
    twitterUrl,
    i18nText,
    theme,
  }: SocialMediaLinksProps) => {
    if (!enabledSocialMedia) return null

    return (
      <React.Fragment>
        <StyledSeparator />
        <LeftRailTitle
          textStyling={theme.widget.helpCenterCollection?.leftRail.title}
        >
          {i18nText.socialLinksTitle}
        </LeftRailTitle>
        <ul>
          {facebookUrl && (
            <WrappedLink href={facebookUrl} content={i18nText.facebook}>
              <FacebookOutline />
            </WrappedLink>
          )}
          {twitterUrl && (
            <WrappedLink href={twitterUrl} content={i18nText.twitter}>
              <TwitterOutline />
            </WrappedLink>
          )}
        </ul>
      </React.Fragment>
    )
  }

  const ChatContainer = ({ hasLivePerson }: ChatContainerProps) => {
    if (hasLivePerson) {
      return (
        <LiveChatWrapper>
          {/* Don't change these class names, GTM uses them to add the LivePerson scripts in */}
          <div className="lp-panel">
            <div id="checkout-lpButtonDiv"></div>
          </div>
        </LiveChatWrapper>
      )
    }

    return null
  }

  return (
    <div className={props.className}>
      <TextContainer>
        <Title textStyling={theme.widget.helpCenterCollection?.title}>
          {i18nText.pageTitle}
        </Title>
        <Subtitle textStyling={theme.widget.helpCenterCollection?.subtitle}>
          {i18nText.pageSubTitle}
        </Subtitle>
        <HelpCenterCollectionSearch
          searchValue={searchValue}
          searchText={i18nText.searchText}
          foundSearchResults={foundSearchResults}
          noResultsFound={i18nText.noResultsFound}
          handleInputOnChange={handleInputOnChange}
          handleClearInput={handleClearInput}
          showNoResultsMessage={showNoResultsMessage}
          resultsRef={resultsRef}
          noResultsMessageRef={noResultsMessageRef}
        />
      </TextContainer>
      <Grid columns={12} rows={1}>
        <StyledGridItem colSpan={[12, 5, 4, 4]} leftGrid={true}>
          <LeftRailTitle
            textStyling={theme.widget.helpCenterCollection?.leftRail.title}
          >
            {i18nText.leftRailTitle}
          </LeftRailTitle>
          <ul>
            {accordionNavItems.map((item, index) => {
              return (
                <StyledLi key={`category-button-${index}`}>
                  <StyledButton
                    textStyling={
                      theme.widget.helpCenterCollection?.leftRail.content
                    }
                    data-testid={`category-button-${index}`}
                    ref={(el) =>
                      itemsRef.current
                        ? (itemsRef.current[index] = el)
                        : undefined
                    }
                    active={item.active}
                    onClick={() => {
                      handleActiveAccordionOnClick(index)
                      dataTracking.sendCategoryTitleHandler(item.title)
                    }}
                    onKeyDown={(e) => handleActiveAccordionOnKeyPress(e, index)}
                  >
                    {item.title}
                  </StyledButton>
                </StyledLi>
              )
            })}
          </ul>
          <StyledSeparator />
          <LeftRailTitle
            textStyling={theme.widget.helpCenterCollection?.leftRail.title}
          >
            {i18nText.customerSupportTitle}
          </LeftRailTitle>
          <ChatAndMessageLinks
            hasLivePerson={hasLivePerson}
            hasAdaChatbot={hasAdaChatbot}
            hasRingCentral={hasRingCentral}
            enabledAppleChat={enabledAppleChat}
            i18nText={i18nText}
          />
          <SocialMediaLinks
            enabledSocialMedia={enabledSocialMedia}
            facebookUrl={facebookUrl}
            twitterUrl={twitterUrl}
            i18nText={i18nText}
            theme={theme}
          />
        </StyledGridItem>

        <StyledGridItem colSpan={[12, 7, 8, 8]} activeSearch={activeSearch}>
          <StyledAccordionContainer
            openedContent={openedContent}
            ref={accordionRef}
            tabIndex={contentTabIndex}
            data-testid={`accordion-container-${activeAccordion?.index}`}
          >
            <BackButton
              data-testid="back-button"
              onClick={() => {
                setOpenedContent(false)
              }}
            >
              <ChevronLeft />
              {i18nText.back}
            </BackButton>

            {enabledAccessibilityBackButton && (
              <AccessibilityBackButtonWrapper showBackButton={showBackButton}>
                <Button
                  data-testid="back-button-accessibility"
                  emphasis="low"
                  onClick={() => {
                    handleBackAction(activeAccordion?.index || 0)
                  }}
                  onKeyDown={(e) =>
                    e.key === 'Enter' &&
                    handleBackAction(activeAccordion?.index || 0)
                  }
                >
                  {i18nText.goBack}
                </Button>
              </AccessibilityBackButtonWrapper>
            )}
            {activeAccordion && (
              <StyledAccordionContent
                key={activeAccordion.index}
                items={activeAccordion.items}
                title={activeAccordion.title}
                titleStyling={
                  theme.widget.helpCenterCollection?.accordion?.title
                }
                alignment={'left' as HorizontalAlignment}
                subtitle={i18nText.accordionSubTitle}
                closeAllOnRender={true}
                contentPadding={true}
                fitTitles={true}
                addTitleRightMarginA11y={showBackButton}
                accordionTitleCallback
              />
            )}

            {!!foundSearchResults?.length && (
              <StyledAccordionSearchContainer
                data-testid="desktop-search-content"
                tabIndex={0}
                aria-label={`${i18nText.searchResults} ${searchValue}`}
                ref={resultsRef}
              >
                {foundSearchResults.map((accordion, index) => (
                  <StyledAccordionSearchContent
                    key={`desktop-search-content-${index}`}
                    items={accordion.items}
                    title={accordion.title}
                    alignment={'left' as HorizontalAlignment}
                    closeAllOnRender={true}
                  />
                ))}
              </StyledAccordionSearchContainer>
            )}

            {showNoResultsMessage && (
              <StyledNotFoundText
                data-testid="no-results-text"
                ref={noResultsMessageRef}
                tabIndex={0}
              >
                <PlatformMessage type="info" text={i18nText.noResultsFound} />
              </StyledNotFoundText>
            )}
          </StyledAccordionContainer>
        </StyledGridItem>
        <ChatContainer hasLivePerson={hasLivePerson} />
      </Grid>
    </div>
  )
}
